/**
 * Transaction Correlator
 *
 * Manages workflow-level tracking and correlation of related user actions.
 * Provides hierarchical organization of actions within workflows.
 */

import { IdGenerator } from './IdGenerator';

import type { ActionContext, WorkflowTracking, WorkflowType } from '../types';
import type { ContextEnricher } from './ContextEnricher';

/**
 * Transaction correlator implementation
 */
export class TransactionCorrelator {
  private static instance: TransactionCorrelator;
  private activeWorkflows = new Map<string, WorkflowTracking>();
  private workflowTransactions = new Map<string, string[]>(); // workflowId -> transactionIds
  private transactionWorkflows = new Map<string, string>(); // transactionId -> workflowId
  private contextEnricher: ContextEnricher;
  private idGenerator: IdGenerator;

  constructor(contextEnricher: ContextEnricher) {
    this.contextEnricher = contextEnricher;
    this.idGenerator = IdGenerator.getInstance();

    // Set up cleanup for abandoned workflows
    this.setupWorkflowCleanup();
  }

  static getInstance(contextEnricher?: ContextEnricher): TransactionCorrelator {
    if (!TransactionCorrelator.instance && contextEnricher) {
      TransactionCorrelator.instance = new TransactionCorrelator(
        contextEnricher,
      );
    }
    return TransactionCorrelator.instance;
  }

  /**
   * Start a new workflow
   */
  startWorkflow(
    workflowType: WorkflowType,
    metadata?: Record<string, any>,
    context?: Partial<ActionContext>,
  ): string {
    const workflowId = this.idGenerator.generateWorkflowId();
    const enrichedContext = this.contextEnricher.enrichContext(context);

    const workflow: WorkflowTracking = {
      workflowId,
      workflowType,
      startTime: performance.now(),
      transactionIds: [],
      metadata,
      context: enrichedContext,
    };

    this.activeWorkflows.set(workflowId, workflow);
    this.workflowTransactions.set(workflowId, []);

    return workflowId;
  }

  /**
   * Add a transaction to a workflow
   */
  addTransactionToWorkflow(workflowId: string, transactionId: string): void {
    const workflow = this.activeWorkflows.get(workflowId);
    if (!workflow) {
      console.warn(`Workflow not found: ${workflowId}`);
      return;
    }

    // Add to workflow's transaction list
    workflow.transactionIds.push(transactionId);

    // Update workflow transactions mapping
    const transactions = this.workflowTransactions.get(workflowId) || [];
    transactions.push(transactionId);
    this.workflowTransactions.set(workflowId, transactions);

    // Update transaction to workflow mapping
    this.transactionWorkflows.set(transactionId, workflowId);

    // Update the workflow in active workflows
    this.activeWorkflows.set(workflowId, workflow);
  }

  /**
   * Complete a workflow
   */
  completeWorkflow(
    workflowId: string,
    success: boolean,
    metadata?: Record<string, any>,
  ): void {
    const workflow = this.activeWorkflows.get(workflowId);
    if (!workflow) {
      console.warn(`Workflow not found: ${workflowId}`);
      return;
    }

    // Update workflow with completion data
    workflow.endTime = performance.now();
    workflow.duration = workflow.endTime - workflow.startTime;
    workflow.success = success;
    workflow.metadata = { ...workflow.metadata, ...metadata };

    // Clean up
    this.cleanupWorkflow(workflowId);
  }

  /**
   * Cancel a workflow
   */
  cancelWorkflow(workflowId: string, reason?: string): void {
    const workflow = this.activeWorkflows.get(workflowId);
    if (!workflow) {
      return;
    }

    // Update workflow with cancellation data
    workflow.endTime = performance.now();
    workflow.duration = workflow.endTime - workflow.startTime;
    workflow.success = false;
    workflow.metadata = {
      ...workflow.metadata,
      cancellationReason: reason,
      cancelled: true,
    };

    // Clean up
    this.cleanupWorkflow(workflowId);
  }

  /**
   * Get workflow for a transaction
   */
  getWorkflowForTransaction(
    transactionId: string,
  ): WorkflowTracking | undefined {
    const workflowId = this.transactionWorkflows.get(transactionId);
    return workflowId ? this.activeWorkflows.get(workflowId) : undefined;
  }

  /**
   * Get all transactions in a workflow
   */
  getWorkflowTransactions(workflowId: string): string[] {
    return this.workflowTransactions.get(workflowId) || [];
  }

  /**
   * Get active workflow count
   */
  getActiveWorkflowCount(): number {
    return this.activeWorkflows.size;
  }

  /**
   * Get workflow type for a given workflow ID
   */
  getWorkflowType(workflowId: string): WorkflowType | null {
    const workflow = this.activeWorkflows.get(workflowId);
    return workflow ? workflow.workflowType : null;
  }

  /**
   * Get active workflows for debugging
   */
  getActiveWorkflows(): WorkflowTracking[] {
    return Array.from(this.activeWorkflows.values());
  }

  /**
   * Update workflow metadata
   */
  updateWorkflowMetadata(
    workflowId: string,
    metadata: Record<string, any>,
  ): void {
    const workflow = this.activeWorkflows.get(workflowId);
    if (workflow) {
      workflow.metadata = { ...workflow.metadata, ...metadata };
      this.activeWorkflows.set(workflowId, workflow);
    }
  }

  /**
   * Check if workflow exists
   */
  hasWorkflow(workflowId: string): boolean {
    return this.activeWorkflows.has(workflowId);
  }

  /**
   * Clean up workflow data
   */
  private cleanupWorkflow(workflowId: string): void {
    const transactionIds = this.workflowTransactions.get(workflowId) || [];

    // Remove transaction to workflow mappings
    transactionIds.forEach(transactionId => {
      this.transactionWorkflows.delete(transactionId);
    });

    // Remove workflow data
    this.activeWorkflows.delete(workflowId);
    this.workflowTransactions.delete(workflowId);
  }

  /**
   * Set up cleanup for abandoned workflows
   */
  private setupWorkflowCleanup(): void {
    setInterval(
      () => {
        const now = performance.now();
        const abandonedWorkflows: string[] = [];

        this.activeWorkflows.forEach((workflow, workflowId) => {
          // Consider workflows abandoned after 2 hours
          if (now - workflow.startTime > 2 * 60 * 60 * 1000) {
            abandonedWorkflows.push(workflowId);
          }
        });

        // Clean up abandoned workflows
        abandonedWorkflows.forEach(workflowId => {
          console.warn(`Cleaning up abandoned workflow: ${workflowId}`);
          this.cancelWorkflow(
            workflowId,
            'Workflow abandoned - automatically cancelled',
          );
        });
      },
      5 * 60 * 1000,
    ); // Check every 5 minutes
  }
}
